/* eslint-disable @typescript-eslint/no-explicit-any */
import EventEmitter2 from 'eventemitter2';

/**
 * AFFiNE `packages/common/nbstore/src/connection/connection.ts` 포팅.
 * refCount 기반 connect/disconnect + 자동 재연결 추상 베이스.
 *
 * 원본과의 의도적 차이는 단 하나 — **재시도 상한(서킷브레이커)**. 원본은 영구 재연결이다.
 * 상세 근거는 `maxRetries` 주석 참고. 그 외는 원본 유지.
 */

// 원본 '../utils/throw-if-aborted' 의 MANUALLY_STOP 를 inline
export const MANUALLY_STOP = 'manually-stop';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'closed';

/**
 * 재시도해도 결과가 같은(=doomed) 연결 실패. 트랜스포트가 이걸 던지면 AutoReconnectConnection은
 * 재연결을 예약하지 않고 즉시 포기한다.
 *
 * 대표 사례 = 만료된 Bearer 토큰(401/403). 방치된 탭에선 토큰이 진짜 죽어 있어 같은 토큰으로
 * 재연결해도 영구히 403이고, 3초 주기 재시도가 서버 로그를 시간당 ~1,200건 오염시켰다.
 * 토큰이 갱신되면 소비자 effect가 재실행되며 새 연결이 만들어져 자동 회복된다.
 */
export class NonRetryableConnectionError extends Error {
  readonly nonRetryable = true;

  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'NonRetryableConnectionError';
  }
}

/** instanceof + duck-typing 둘 다 허용 (번들 경계로 프로토타입이 갈리는 경우 대비) */
export const isNonRetryableConnectionError = (err: unknown): boolean =>
  err instanceof NonRetryableConnectionError ||
  (!!err && typeof err === 'object' && (err as { nonRetryable?: boolean }).nonRetryable === true);

export interface Connection<T = any> {
  readonly status: ConnectionStatus;
  readonly error?: Error;
  readonly inner: T;
  connect(): void;
  disconnect(): void;
  waitForConnected(signal?: AbortSignal): Promise<void>;
  onStatusChanged(cb: (status: ConnectionStatus, error?: Error) => void): () => void;
}

export abstract class AutoReconnectConnection<T = any> implements Connection<T> {
  private readonly event = new EventEmitter2({
    maxListeners: 100,
  });
  private _inner: T | undefined = undefined;
  private _status: ConnectionStatus = 'idle';
  private _error: Error | undefined = undefined;
  retryDelay = 3000;
  connectingTimeout = 15000;
  /**
   * ⚠️ AFFiNE 원본과의 의도적 차이 (원본엔 재시도 상한이 없다 — 영구 재연결).
   *
   * 원본의 공유 연결은 endpoint당 1개이고 앱 수명 내내 유지되므로 영구 재시도가 맞다. 우리 SSE는
   * job(resource)당 단명이고 Bearer 토큰으로 인증하므로, 실패가 "일시적"이라는 원본의 전제가 깨진다.
   * → 연속 실패가 이 횟수를 넘으면 재연결을 포기하고 force-disconnect('closed')한다.
   *   share()가 'closed' 연결을 map에서 제거하므로, 토큰 갱신 등으로 소비자 effect가 재실행되면
   *   새 연결이 만들어져 자동 회복된다.
   */
  maxRetries = 5;
  private refCount = 0;
  private retryCount = 0;
  private _retriesExhausted = false;
  private connectingAbort?: AbortController;
  private reconnectingAbort?: AbortController;

  get shareId(): string | undefined {
    return undefined;
  }

  /**
   * 재연결을 포기했는지. true면 이 연결 객체는 다시 살아나지 않는다(새 인스턴스가 필요).
   * 소비자는 이 값으로 "끊김(일시적, 폴링으로 버팀)"과 "doomed(폴링해도 같은 실패)"를 구분한다.
   */
  get retriesExhausted(): boolean {
    return this._retriesExhausted;
  }

  get maybeConnection() {
    return this._inner;
  }

  get inner(): T {
    if (this._inner === undefined) {
      throw new Error(`Connection ${this.constructor.name} has not been established.`);
    }
    return this._inner;
  }

  get status() {
    return this._status;
  }

  get error() {
    return this._error;
  }

  protected set error(error: Error | undefined) {
    this.handleError(error);
  }

  private setStatus(status: ConnectionStatus, error?: Error) {
    const shouldEmit = status !== this._status || error !== this._error;
    this._status = status;
    if (error || status === 'connected') {
      this._error = error;
    }
    if (shouldEmit) {
      this.emitStatusChanged(status, this._error);
    }
  }

  protected abstract doConnect(signal?: AbortSignal): Promise<T>;
  protected abstract doDisconnect(conn: T): void;

  private innerConnect() {
    if (this.status !== 'connecting') {
      this.setStatus('connecting');
      const connectingAbort = new AbortController();
      this.connectingAbort = connectingAbort;
      const signal = connectingAbort.signal;
      const timeout = setTimeout(() => {
        if (!signal.aborted) {
          this.handleError(new Error('connecting timeout'));
        }
      }, this.connectingTimeout);
      this.doConnect(signal)
        .then((value) => {
          clearTimeout(timeout);
          if (!signal.aborted) {
            this._inner = value;
            this.retryCount = 0; // 연결 성공 → 서킷브레이커 카운터 리셋
            this.setStatus('connected');
          } else {
            try {
              this.doDisconnect(value);
            } catch (error) {
              console.error('failed to disconnect', error);
            }
          }
        })
        .catch((error) => {
          if (!signal.aborted) {
            clearTimeout(timeout);
            console.error('failed to connect', error);
            this.handleError(error as any);
          }
        });
    }
  }

  private innerDisconnect() {
    this.connectingAbort?.abort(MANUALLY_STOP);
    this.reconnectingAbort?.abort(MANUALLY_STOP);
    try {
      if (this._inner) {
        this.doDisconnect(this._inner);
      }
    } catch (error) {
      console.error('failed to disconnect', error);
    }
    this.reconnectingAbort = undefined;
    this.connectingAbort = undefined;
    this._inner = undefined;
  }

  private handleError(reason?: Error) {
    this.innerDisconnect();
    // if the connection is closed, do not reconnect
    if (this.status === 'closed') {
      return;
    }

    // ── 서킷브레이커 (AFFiNE 원본에 없는 추가 — maxRetries 주석 참고) ──
    // doomed 실패(만료 토큰 401/403)는 첫 실패에, 그 외 실패는 연속 maxRetries 초과 시 포기한다.
    // 포기 판정을 setStatus('error') 전에 세워야 소비자가 'error' 콜백에서 retriesExhausted로
    // "폴링으로 버틸 끊김"인지 "폴링해도 같은 실패"인지 구분할 수 있다.
    this.retryCount += 1;
    this._retriesExhausted =
      isNonRetryableConnectionError(reason) || this.retryCount > this.maxRetries;

    this.setStatus('error', reason);

    if (this._retriesExhausted) {
      console.warn('connection gave up reconnecting', {
        retryCount: this.retryCount,
        reason,
      });
      // refCount 무시 즉시 'closed' → 예약된 재연결 없음 + share()가 map에서 제거.
      // (_error는 setStatus가 'closed'에서 덮어쓰지 않으므로 실패 사유는 그대로 남는다)
      this.disconnect(true);
      return;
    }

    console.error('connection error, will reconnect', reason);
    this.reconnectingAbort = new AbortController();
    const signal = this.reconnectingAbort.signal;
    const timeout = setTimeout(() => {
      if (!signal.aborted) {
        this.innerConnect();
      }
    }, this.retryDelay);
    signal.addEventListener('abort', () => {
      clearTimeout(timeout);
    });
  }

  connect() {
    this.refCount++;
    if (this.refCount === 1) {
      // 첫 참조 = 새 소비자 → 서킷브레이커 예산을 새로 준다.
      // (share()가 'closed' 연결을 제거하므로 보통 새 인스턴스지만, 공유되지 않는 연결이
      //  포기 후 재사용되는 경로에서도 재연결이 가능해야 한다)
      this.retryCount = 0;
      this._retriesExhausted = false;
      this.innerConnect();
    }
  }

  disconnect(force?: boolean) {
    if (force) {
      this.refCount = 0;
    } else {
      this.refCount = Math.max(this.refCount - 1, 0);
    }
    if (this.refCount === 0) {
      this.innerDisconnect();
      this.setStatus('closed');
    }
  }

  waitForConnected(signal?: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
      if (this.status === 'connected') {
        resolve();
        return;
      }
      const off = this.onStatusChanged((status) => {
        if (status === 'connected') {
          resolve();
          off();
        }
      });
      signal?.addEventListener('abort', (reason) => {
        reject(reason);
        off();
      });
    });
  }

  onStatusChanged(cb: (status: ConnectionStatus, error?: Error) => void): () => void {
    this.event.on('statusChanged', cb);
    return () => {
      this.event.off('statusChanged', cb);
    };
  }

  private readonly emitStatusChanged = (status: ConnectionStatus, error?: Error) => {
    this.event.emit('statusChanged', status, error);
  };
}

export class DummyConnection implements Connection<undefined> {
  readonly status: ConnectionStatus = 'connected';
  readonly inner: undefined;

  connect(): void {
    return;
  }
  disconnect(): void {
    return;
  }
  waitForConnected(_signal?: AbortSignal): Promise<void> {
    return Promise.resolve();
  }
  onStatusChanged(_cb: (status: ConnectionStatus, error?: Error) => void): () => void {
    return () => {};
  }
}
